import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Newspaper, RefreshCw, Shield, Landmark, ArrowLeftRight, MapPin, Leaf, ExternalLink, Globe2, Filter } from "lucide-react";
import { motion } from "framer-motion";
import PageIntro from "../components/PageIntro";

const categoryIcons = {
  tax: Landmark,
  regulation: Shield,
  infrastructure: MapPin,
  visa: Shield,
  currency: ArrowLeftRight,
  esg: Leaf,
};

const impactColors = {
  high: "text-red-400 bg-red-500/10 border-red-500/20",
  medium: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  low: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
};

export default function RegulatoryPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [emptyReason, setEmptyReason] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState(null);

  const fetchNews = useCallback(async () => {
    try {
      const { data } = await api.get("/news");
      setArticles(data.articles || []);
      setEmptyReason(data.empty_reason || null);
      setEmptyMessage(data.message || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/news/refresh");
      setArticles(data.articles || []);
      setEmptyReason(data.empty_reason || null);
      setEmptyMessage(data.message || null);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  // Locations actually present in the user's news feed — drives which filter chips appear
  const presentLocations = [...new Set(articles.map(a => a.location).filter(Boolean))];

  // Sort: HIGH impact first, then MEDIUM, then LOW. Tie-break by date desc.
  const IMPACT_RANK = { high: 0, medium: 1, low: 2 };
  const ranked = [...articles].sort((a, b) => {
    const ra = IMPACT_RANK[a.impact] ?? 3;
    const rb = IMPACT_RANK[b.impact] ?? 3;
    if (ra !== rb) return ra - rb;
    return (b.date || "").localeCompare(a.date || "");
  });

  const filtered = filter === "all" ? ranked : ranked.filter(a => a.category === filter || a.location === filter);
  const categories = [...new Set(articles.map(a => a.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] px-4 text-center" data-testid="sentinel-loading">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-white/85 text-sm mt-6 font-sans">Synthesising regulatory updates…</p>
        <p className="text-white/65 text-xs mt-2 max-w-xs leading-relaxed">
          We're scanning real-time regulatory feeds across your jurisdictions.
          This can take up to <span className="text-[#D4AF37]/80">30 seconds</span> on first load — subsequent visits are instant from cache.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="regulatory-page">
      <PageIntro
        pageKey="sentinel"
        title="Regulatory Sentinel"
        subtitle="Real, sourced news on tax, residency and property law in your jurisdictions."
        bullets={[
          { icon: Globe2, label: "Real publishers only", text: "Pulled directly from reliable sources. Click any source to open the article." },
          { icon: Newspaper, label: "Germany items in German", text: "We surface Germany-specific news in the original German legal terminology — preserving accuracy." },
          { icon: Filter, label: "Filter by topic", text: "Use the chips at the top to focus on Tax, Regulation, Visa, ESG or a specific country." },
        ]}
      />
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">Intelligence</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-white tracking-tight">Regulatory Sentinel</h1>
          <p className="text-white/60 text-sm mt-1">AI-curated regulatory news affecting your properties</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-news-btn"
          className="bg-transparent border border-white/20 text-white px-5 py-2.5 text-sm hover:border-white/40 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing..." : "Refresh Feed"}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="news-filters">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 text-xs border transition-all ${
            filter === "all" ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]" : "border-white/10 text-white/60 hover:border-white/20"
          }`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 text-xs border transition-all capitalize ${
              filter === cat ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]" : "border-white/10 text-white/60 hover:border-white/20"
            }`}
          >
            {cat}
          </button>
        ))}
        {presentLocations.includes("dubai") && (
          <button
            onClick={() => setFilter("dubai")}
            data-testid="filter-dubai"
            className={`px-3 py-1.5 text-xs border transition-all ${
              filter === "dubai" ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]" : "border-white/10 text-white/60 hover:border-white/20"
            }`}
          >
            Dubai
          </button>
        )}
        {presentLocations.includes("north_cyprus") && (
          <button
            onClick={() => setFilter("north_cyprus")}
            data-testid="filter-north-cyprus"
            className={`px-3 py-1.5 text-xs border transition-all ${
              filter === "north_cyprus" ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]" : "border-white/10 text-white/60 hover:border-white/20"
            }`}
          >
            North Cyprus
          </button>
        )}
        {presentLocations.includes("germany") && (
          <button
            data-testid="filter-germany"
            onClick={() => setFilter("germany")}
            className={`px-3 py-1.5 text-xs border transition-all ${
              filter === "germany" ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]" : "border-white/10 text-white/60 hover:border-white/20"
            }`}
          >
            Germany
          </button>
        )}
      </div>

      {/* Articles */}
      <div className="space-y-4">
        {filtered.length > 0 ? filtered.map((article, i) => {
          const Icon = categoryIcons[article.category] || Newspaper;
          const impactClass = impactColors[article.impact] || impactColors.low;

          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#0A0A0A] border border-white/[0.10] p-5 hover:border-white/[0.1] transition-colors"
              data-testid={`news-article-${i}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/[0.03] border border-white/[0.10] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-white text-sm font-medium leading-snug">{article.title}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border flex-shrink-0 ${impactClass}`}>
                      {article.impact}
                    </span>
                  </div>
                  <p className="text-white/65 text-sm mt-2 leading-relaxed">{article.summary}</p>

                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    {article.source && article.source_url ? (
                      <a
                        href={article.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Read the full article on ${article.source}`}
                        data-testid={`news-source-link-${i}`}
                        className="inline-flex items-center gap-1 text-white/65 hover:text-[#D4AF37] text-[10px] transition-colors group"
                      >
                        <span>Source: {article.source}</span>
                        <ExternalLink className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                      </a>
                    ) : article.source ? (
                      <span className="text-white/60 text-[10px]" title="Source URL unavailable">Source: {article.source}</span>
                    ) : null}
                    {article.date && (
                      <span className="text-white/60 text-[10px]">{article.date}</span>
                    )}
                    {article.location && (
                      <span className="text-white/60 text-[10px] uppercase">{article.location.replace("_", " ")}</span>
                    )}
                    {article.category && (
                      <span className="bg-white/5 text-white/60 text-[10px] px-2 py-0.5 uppercase tracking-wider">
                        {article.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        }) : (
          <div className="bg-[#0A0A0A] border border-white/[0.10] p-12 text-center">
            <Newspaper className="w-12 h-12 text-white/10 mx-auto mb-3" strokeWidth={1} />
            {emptyReason === "no_assets" ? (
              <>
                <p className="text-white/60 text-sm mb-1">No properties yet</p>
                <p className="text-white/60 text-xs max-w-md mx-auto">
                  {emptyMessage || "Add a property to your portfolio to see regulatory news for that jurisdiction."}
                </p>
              </>
            ) : (
              <>
                <p className="text-white/65 text-sm mb-1">No news articles available</p>
                <p className="text-white/55 text-xs">Try refreshing the feed or adjust your filter.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
